import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, Link as RouterLink, useMatches } from 'react-router-dom';
import ashlinkLogoDark from '../../../assets/ASHlink_Logo_Dark.svg';
import { GetAshlinkPublicUserRedirectionsFn } from '../../../data/apiCalls';
import useIsMobile from '../../../hooks/useIsMobile';
import {
	getHeaderNavigationItems,
	getHomePagePath,
} from '../../../scripts/navigationHelper';
import {
	ACCOUNT_ACTIVATION_URL,
	HOME_URL,
	LOGIN_URL,
} from '../../../scripts/navigationItems';
import MobileNavMenu from './MobileNavMenu';
import NavigationBar from './NavigationBar';

const Header = props => {
	const isMobile = useIsMobile();
	const matches = useMatches();

	// hideHeader hides everything in the header except the ASHLink Logo
	const hideHeader = matches.some(
		match => match.handle && match.handle.hideHeader
	);

	const {
		data: {
			status,
			data: { resultCode, data: { ashlinkUserRedirections } = {} } = {},
		} = {},
		isLoading,
		isError,
	} = useQuery(
		['userRedirections'],
		() => GetAshlinkPublicUserRedirectionsFn(),
		{
			staleTime: Infinity,
		}
	);
	const [navigationItems, setNavigationItems] = useState([]);
	const [homePagePath, setHomePagePath] = useState(HOME_URL);
	useEffect(() => {
		if (ashlinkUserRedirections !== undefined) {
			setNavigationItems(
				getHeaderNavigationItems(ashlinkUserRedirections)
			);
			setHomePagePath(getHomePagePath(ashlinkUserRedirections));
		}
	}, [ashlinkUserRedirections]);

	const [isApiError, setIsApiError] = useState(false);
	const navigate = useNavigate();
	useEffect(() => {
		if (
			isError ||
			(status && status !== 200) ||
			(resultCode && resultCode !== 0)
		) {
			setIsApiError(true);
			navigate('/error');
		} else {
			setIsApiError(false);
		}
	}, [isError, status, resultCode]);

	return (
		<AppBar
			{...props}
			position='static'
			elevation={0}
			sx={{
				backgroundColor: 'background.default',
			}}
		>
			<Box sx={{ boxShadow: { xs: 1, md: 0 }, zIndex: 100 }}>
				<Box sx={{ px: 3 }}>
					<Toolbar
						sx={{
							justifyContent: 'space-between',
							py: 0,
							px: { xs: 0, sm: 0, md: 4 },
							minHeight: 64,
							height: 64,
						}}
					>
						<Stack
							direction='row'
							sx={{
								width: '100%',
							}}
						>
							{isMobile && !hideHeader && !isApiError && (
								<MobileNavMenu
									navigationItems={navigationItems}
									isLoading={isLoading}
								/>
							)}
							{homePagePath && (
								<Stack
									direction='row'
									sx={{
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									{hideHeader ? (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												p: 1,
											}}
										>
											<img
												src={ashlinkLogoDark}
												alt='ASHLink Logo'
												width='90px'
											/>
										</Box>
									) : (
										<Button
											component={Link}
											href={homePagePath}
											target='_self'
											rel='noreferrer'
										>
											<img
												src={ashlinkLogoDark}
												alt='ASHLink Logo'
												width='90px'
											/>
										</Button>
									)}
									{!hideHeader && (
										<div
											role='navigation'
											aria-label='User account navigation'
										>
											<Stack direction='row' spacing={1}>
												<Button
													component={RouterLink}
													to={ACCOUNT_ACTIVATION_URL}
													size='small'
													sx={{ alignSelf: 'center' }}
												>
													Create Account
												</Button>
												<Button
													variant='contained'
													component={RouterLink}
													to={LOGIN_URL}
													size='small'
													sx={{ alignSelf: 'center' }}
												>
													Log in
												</Button>
											</Stack>
										</div>
									)}
								</Stack>
							)}
						</Stack>
					</Toolbar>
				</Box>
			</Box>
			{!isMobile && !isApiError && !hideHeader && (
				<NavigationBar items={navigationItems} isLoading={isLoading} />
			)}
		</AppBar>
	);
};

export default Header;
